import { useQuery } from '@tanstack/react-query'
import { getAssignments, type Visit } from '../../lib/services/visits'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { MapPin, ChevronRight, Building2, FileText, History, Upload } from 'lucide-react'
import { authFailureMessage, isAuthFailure, shouldRetryRequest } from '../../lib/errors'
import { useAuth } from '../../context/AuthContext'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'

/**
 * Visits List Page [FE.1.1]
 * Displays assigned visits for the current surveyor.
 */
export function VisitsListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { signOut } = useAuth()
  const { data: visits, isLoading, error } = useQuery({
    queryKey: ['visits', 'assignments'],
    queryFn: getAssignments,
    retry: shouldRetryRequest,
  })
  const { data: facilities, isLoading: facilitiesLoading } = useQuery({
    queryKey: ['visits', 'facilities'],
    queryFn: getFacilities,
    retry: shouldRetryRequest,
  })

  if (isLoading || facilitiesLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-neutral-900"></div>
      </div>
    )
  }

  if (error) {
    const isAuthError = isAuthFailure(error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-medium">{isAuthError ? 'Session validation failed' : 'Failed to load visits'}</p>
        <p className="text-sm">{isAuthError ? authFailureMessage(error) : (error as Error).message}</p>
        {isAuthError && (
          <button
            className="mt-2 text-sm underline"
            onClick={async () => {
              await signOut()
              navigate('/login')
            }}
          >
            Sign in again
          </button>
        )}
      </div>
    )
  }

  const today = new Date().toISOString().split('T')[0]
  const sortedVisits = [...(visits || [])].sort((a, b) => a.visit_date.localeCompare(b.visit_date))
  const facilityRows = facilities || []
  const requestedFacilityId = searchParams.get('facility') || ''
  const selectedFacilityId = requestedFacilityId || (facilityRows.length === 1 ? facilityRows[0].facility_id : '')
  const selectedFacility = facilityRows.find((facility) => facility.facility_id === selectedFacilityId) ?? null
  const selectedVisits = selectedFacilityId
    ? sortedVisits.filter((visit: Visit) => visit.facility_id === selectedFacilityId)
    : []
  const todayVisits = selectedVisits.filter((v: Visit) => v.visit_date.startsWith(today))
  const upcomingVisits = selectedVisits.filter((v: Visit) => !v.visit_date.startsWith(today) && v.status !== 'released' && v.status !== 'archived')
  const historicalVisits = selectedVisits.filter((v: Visit) => v.status === 'released' || v.status === 'archived')
  const activeVisit = selectedVisits.find((visit) => visit.status === 'in_progress') ?? selectedVisits[0] ?? null

  const statusBadgeClass = (status: Visit['status']) => {
    if (status === 'in_progress' || status === 'in_review') return 'badge-in-progress'
    if (status === 'released') return 'badge-released'
    if (status === 'archived') return 'badge-archived'
    return 'badge-scheduled'
  }

  const facilityLocation = (facility: FacilityOption) =>
    [facility.address, facility.city, facility.state, facility.zip_code].filter(Boolean).join(', ') || 'Location not configured'

  const handleFacilityChange = (facilityId: string) => {
    const next = new URLSearchParams(searchParams)
    if (facilityId) {
      next.set('facility', facilityId)
    } else {
      next.delete('facility')
    }
    setSearchParams(next)
  }

  const VisitRow = ({ visit }: { visit: Visit }) => (
    <Link
      to={`/visits/${visit.id}`}
      className="group grid grid-cols-1 gap-3 border-b border-slate-100 px-4 py-3 transition-all hover:bg-slate-50 hover:shadow-sm sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4"
    >
      <div>
        <p className="text-sm font-bold text-slate-800 group-hover:text-[var(--brand-primary)]">{visit.facility_name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
          <MapPin size={12} />
          {visit.address}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <span className={statusBadgeClass(visit.status)}>{visit.status.replace('_', ' ')}</span>
        <ChevronRight size={16} className="text-slate-300 group-hover:text-[var(--brand-primary)]" />
      </div>
    </Link>
  )

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-20">
      <section className="v11-workspace-header">
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Visits / Facilities
        </p>
        <h1 className="mt-1 font-brand text-2xl font-extrabold text-[var(--brand-deep)]">
          Select a facility to focus the visit workspace
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Facility details, prestudy documents, current visits, and historical reports stay scoped to the selected facility.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <label className="block">
            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Facility</span>
            <select
              value={selectedFacilityId}
              onChange={(event) => handleFacilityChange(event.target.value)}
              className="mt-1 block w-full rounded-2xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition-colors focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-blue-100"
            >
              <option value="">Choose a facility</option>
              {facilityRows.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>
                  {facility.facility_name}
                </option>
              ))}
            </select>
          </label>
          <span className="rounded-2xl border border-[var(--brand-border)] bg-white px-4 py-3 text-sm font-bold text-[var(--brand-deep)]">
            {selectedFacility ? `${selectedVisits.length} visit${selectedVisits.length !== 1 ? 's' : ''}` : `${facilityRows.length} facilities`}
          </span>
        </div>
      </section>

      {!selectedFacility && (
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {facilityRows.map((facility) => {
            const visitCount = sortedVisits.filter((visit) => visit.facility_id === facility.facility_id).length
            return (
              <button
                key={facility.facility_id}
                type="button"
                onClick={() => handleFacilityChange(facility.facility_id)}
                className="v11-dashboard-card p-5 text-left"
              >
                <div className="flex items-start gap-3">
                  <Building2 size={20} className="mt-0.5 text-[var(--brand-secondary)]" />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-brand text-base font-extrabold text-[var(--brand-deep)]">{facility.facility_name}</h2>
                    <p className="mt-1 text-sm text-slate-500">{facilityLocation(facility)}</p>
                    {facility.specialties.length > 0 && (
                      <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                        {facility.specialties.join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">
                    {visitCount}
                  </span>
                </div>
              </button>
            )
          })}
        </section>
      )}

      {selectedFacility && (
        <>
          <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="v11-section p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">Facility briefing</p>
                  <h2 className="mt-1 font-brand text-xl font-extrabold text-[var(--brand-deep)]">{selectedFacility.facility_name}</h2>
                  <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                    <MapPin size={14} />
                    {facilityLocation(selectedFacility)}
                  </p>
                </div>
                <span className="badge-scheduled">{selectedFacility.facility_type || 'Facility'}</span>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <div className="v11-mini-metric text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Admin / DON</p>
                  <p className="mt-1 text-sm font-bold text-slate-800">{selectedFacility.contact_name || 'Not configured'}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{selectedFacility.contact_email || selectedFacility.contact_phone || 'No contact listed'}</p>
                </div>
                <div className="v11-mini-metric text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Specialties</p>
                  <p className="mt-1 text-sm font-bold text-slate-800">
                    {selectedFacility.specialties.length > 0 ? selectedFacility.specialties.join(', ') : 'Not configured'}
                  </p>
                </div>
                <div className="v11-mini-metric text-left">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Visit scope</p>
                  <p className="mt-1 text-sm font-bold text-slate-800">
                    {activeVisit?.visit_type.replace('_', ' ') ?? 'No active visit'}
                  </p>
                </div>
              </div>

              {activeVisit && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to={`/visits/${activeVisit.id}`} className="btn-primary px-4 py-2 text-sm">
                    Open Visit
                  </Link>
                  <Link to={`/visits/${activeVisit.id}/survey/documents`} className="btn-ghost px-4 py-2 text-sm">
                    <FileText size={14} className="mr-1.5 inline" />
                    Review Prestudy Uploads
                  </Link>
                  <Link to={`/visits/${activeVisit.id}/prestudy/create`} className="btn-ghost px-4 py-2 text-sm">
                    <Upload size={14} className="mr-1.5 inline" />
                    Request Uploads
                  </Link>
                </div>
              )}
            </div>

            {facilityRows.length > 1 && (
              <div className="v11-map-card">
                <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-primary)]">Today map</p>
                <p className="mt-1 text-sm text-slate-600">Portfolio location context for multi-site visit planning.</p>
                {facilityRows.slice(0, 6).map((facility, index) => {
                  const positions = [
                    ['24%', '30%'],
                    ['68%', '38%'],
                    ['48%', '70%'],
                    ['82%', '72%'],
                    ['34%', '56%'],
                    ['58%', '22%'],
                  ]
                  const [left, top] = positions[index]
                  return (
                    <span
                      key={facility.facility_id}
                      className={`v11-map-pin ${facility.facility_id === selectedFacility.facility_id ? 'bg-[var(--brand-accent)]' : ''}`}
                      style={{ left, top }}
                      title={facility.facility_name}
                    />
                  )
                })}
              </div>
            )}
          </section>

          <section className="v11-kpi-grid">
            <div className="v11-kpi-card v11-kpi-accent-teal">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Today</p>
              <p className="mt-2 font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{todayVisits.length}</p>
              <p className="mt-1 text-xs text-slate-500">Scheduled visits</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-gold">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Upcoming</p>
              <p className="mt-2 font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{upcomingVisits.length}</p>
              <p className="mt-1 text-xs text-slate-500">Future visits</p>
            </div>
            <div className="v11-kpi-card">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Historical</p>
              <p className="mt-2 font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{historicalVisits.length}</p>
              <p className="mt-1 text-xs text-slate-500">Released or archived</p>
            </div>
          </section>
        </>
      )}

      {selectedFacility && (
        <section className="v11-section">
          <div className="v11-section-header">
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-600">Today</h3>
          </div>
          {todayVisits.length > 0 ? (
            todayVisits.map((visit) => <VisitRow key={visit.id} visit={visit} />)
          ) : (
            <div className="px-4 py-6 text-sm text-slate-500">No visits scheduled for today.</div>
          )}
        </section>
      )}

      {selectedFacility && (
        <section className="v11-section">
          <div className="v11-section-header">
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-600">Upcoming</h3>
          </div>
          {upcomingVisits.length > 0 ? (
            upcomingVisits.map((visit) => <VisitRow key={visit.id} visit={visit} />)
          ) : (
            <div className="px-4 py-6 text-sm text-slate-500">No upcoming visits.</div>
          )}
        </section>
      )}

      {selectedFacility && (
        <section className="v11-section">
          <div className="v11-section-header">
            <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-slate-600">
              <History size={14} />
              Historical Visits & Reports
            </h3>
          </div>
          {historicalVisits.length > 0 ? (
            historicalVisits.map((visit) => <VisitRow key={visit.id} visit={visit} />)
          ) : (
            <div className="px-4 py-6 text-sm text-slate-500">No released or archived visits for this facility yet.</div>
          )}
        </section>
      )}
    </div>
  )
}
